
import { useState } from "react";
import { User } from "@/lib/types";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

interface TestNotificationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedUser: User | null;
  onSendTestNotification: (options: {
    notificationType: "reminder" | "system" | "info";
    notificationTitle: string;
    notificationMessage: string;
    sendEmail: boolean;
  }) => Promise<void>;
  sendingState: "idle" | "sending" | "success" | "error";
}

export function TestNotificationDialog({
  open,
  onOpenChange,
  selectedUser,
  onSendTestNotification,
  sendingState
}: TestNotificationDialogProps) {
  const [notificationType, setNotificationType] = useState<"reminder" | "system" | "info">("system");
  const [notificationTitle, setNotificationTitle] = useState("Test Notification");
  const [notificationMessage, setNotificationMessage] = useState("This is a test notification.");
  const [sendEmail, setSendEmail] = useState(false);

  const handleSendTest = () => {
    onSendTestNotification({
      notificationType,
      notificationTitle,
      notificationMessage,
      sendEmail
    });
  };

  // Helper to determine button variant based on sending state
  const getButtonVariant = () => {
    switch (sendingState) {
      case "error":
        return "destructive";
      case "success":
        return "default";
      default:
        return "default";
    }
  };
  
  // Helper to determine button text based on sending state
  const getButtonText = () => {
    switch (sendingState) {
      case "sending":
        return "Sending...";
      case "success":
        return "Sent!";
      case "error":
        return "Failed to send!";
      default:
        return sendEmail ? "Send Test Email" : "Send Test Notification";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send Test to {selectedUser?.name || selectedUser?.email}</DialogTitle>
          <DialogDescription>
            Customize and send a test notification or email to this user
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="flex items-center space-x-2">
            <Switch 
              id="send-email" 
              checked={sendEmail} 
              onCheckedChange={setSendEmail} 
            />
            <Label htmlFor="send-email">Send as email instead of in-app notification</Label>
          </div>
          
          {!sendEmail && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Notification Type</label>
              <Select 
                value={notificationType} 
                onValueChange={(value) => setNotificationType(value as "reminder" | "system" | "info")}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="reminder">Reminder</SelectItem>
                  <SelectItem value="system">System</SelectItem>
                  <SelectItem value="info">Info</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          
          <div className="space-y-2">
            <label className="text-sm font-medium">{sendEmail ? "Email Subject" : "Title"}</label>
            <Input 
              value={notificationTitle}
              onChange={(e) => setNotificationTitle(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">{sendEmail ? "Email Message" : "Message"}</label>
            <Input 
              value={notificationMessage}
              onChange={(e) => setNotificationMessage(e.target.value)}
            />
          </div>
          
          {sendEmail && selectedUser?.email && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Recipient Email</label>
              <Input 
                value={selectedUser.email}
                readOnly
                disabled
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button 
            onClick={handleSendTest} 
            disabled={sendingState === "sending"}
            variant={getButtonVariant()}
          >
            {getButtonText()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
